import Helpers from './../helpers';
import Native from './native';

export default class Prebid {

  /**
   * Prepares the slots for submission to prebid
   *
   * @param adSlots              - array of ad slot objects defined earlier
   * @param fluidSlotDefinitions - Prebid responsive slot definitions config
   * @returns {Promise<[]>}      - array of ad slots in format expected by prebid excluding the legacy off page slots
   */
  static async prepSlotsForAuction( adSlots, fluidSlotDefinitions ) {

    let formattedAdSlots = [];

    for ( let i = 0; i < adSlots.length; i++ ) {

      // Process legacy & responsive slots, Ignoring off page & fluid slots
      if ( ! adSlots[i].offPage && adSlots[i].type !== 'fluid-native' ) {

        formattedAdSlots.push( {
          code: adSlots[i].id,
          mediaTypes: {
            banner: {
              sizes: adSlots[i].bidSizes.arrays
            }
          },
          bids: await this.setCustomBidderTargeting( adSlots[i] )
        } );

      }

      // Process fluid slots https://docs.prebid.org/prebid/native-implementation.html
      if ( adSlots[i].type === 'fluid-native' ) {

        let mediaTypes = ( await Native.getSlot( adSlots[i].id, fluidSlotDefinitions ) ).mediaTypes;

        let viewport = Helpers.getViewportDimentions();
        let hostname = ADVERTISING_PAGE_DATA.hostname || document.location.origin;

        // Set render url dynamically https://docs.prebid.org/prebid/native-implementation.html#43-implementing-the-custom-renderer-scenario
        mediaTypes.native.rendererUrl = hostname + '/advertising/render?slot_id=' + adSlots[i].id + '&viewport_height=' + viewport.height + '&viewport_width=' + viewport.width;

        formattedAdSlots.push( {
          code: adSlots[i].id,
          mediaTypes: mediaTypes,
          bids: await this.setCustomBidderTargeting( adSlots[i] )
        } );
      }

    }

    return formattedAdSlots;
  }

  /**
   * Sets custom targeting values on the bidder object to be passed to prebid
   *
   * @param adSlot          - the ad slot object
   * @returns {Promise<[]>} - the bidders array augmented with custom targeting data
   */
  static async setCustomBidderTargeting( adSlot ) {

    let bidders = adSlot.bidders;

    for ( let i = 0; i < bidders.length; i++ ) {

      if ( bidders[i].bidder === 'ozone' ) {

        let placementId = bidders[i].params.placementId;
        let publisherId = bidders[i].params.publisherId;
        let siteId      = bidders[i].params.siteId;

        // Define customData object
        let customData = [ {
          'settings': {},
          'targeting': {
            type: adSlot.type,
            position: adSlot.position
          }
        } ];

        if ( adSlot.legacyTargeting ) {

          // Set legacy targeting values
          for ( let ii = 0; ii < adSlot.legacyTargeting.length; ii++ ) {
            customData[0].targeting[adSlot.legacyTargeting[ii].key] = adSlot.legacyTargeting[ii].value;
          }

        }

        // Set page targeting values
        for ( const [ key, value ] of Object.entries( ADVERTISING_PAGE_DATA ) ) {
          customData[0].targeting[key] = value;
        }

        // Define augmented ozone bidder params object
        bidders[i].params = {
          placementId: placementId,
          publisherId: publisherId,
          siteId: siteId,
          customData: customData
        };

      }

      if ( bidders[i].bidder === 'improvedigital' ) {

        let placementId = bidders[i].params.placementId;

        // Define customData object
        // Improve digital have advised they expect all values in key value pairs to be arrays here.
        let keyValues = {
          type: [ adSlot.type ],
          position: [ adSlot.position ]
        };

        if ( adSlot.legacyTargeting ) {

          // Set legacy targeting values
          for ( let ii = 0; ii < adSlot.legacyTargeting.length; ii++ ) {
            keyValues[adSlot.legacyTargeting[ii].key] = adSlot.legacyTargeting[ii].value;
          }

        }

        // Set page targeting values
        for ( let [ key, value ] of Object.entries( ADVERTISING_PAGE_DATA ) ) {

          // Improve digital have advised they expect all values in key value pairs to be arrays here.
          if ( typeof value === 'string' || value instanceof String ) {
            value = [ value ];
          }

          keyValues[key] = value;
        }

        // Define augmented improve digital bidder params object
        bidders[i].params = {
          placementId: placementId,
          keyValues: keyValues
        };

      }

    }

    return bidders;
  }

  /**
   * Gets the bidder data for a particular set of ad sizes
   *
   * @param sizes               - the ad sizes
   * @param placements          - Responsive Prebid placement config
   * @returns {Promise<[]|*[]>} - the matched bidders
   */
  static async getAllBidders( sizes, placements ) {

    // Get the bidder data for the relevant zone
    const bidders = await this.getAvailableBidders( ADVERTISING_PAGE_DATA.zone, placements );

    // Return empty if there are no available bidders defined
    if ( ! bidders ) {
      return [];
    }

    const matchedBidders = [];

    for ( let i = 0; i < sizes.length; i++ ) {

      // Identify the relevant bidders for each ad size
      const matched = await this.getBidders( sizes[i], bidders );

      /**
       * Push each matched bidder to the bidders array if unique and
       * add any custom targeting data to the bid if required
       */
      for ( let ii = 0; ii < matched.length; ii++ ) {
        if ( matchedBidders.length ) {
          let exists = false;

          for ( let iii = 0; iii < matchedBidders.length; iii++ ) {
            if ( await Helpers.objectsEqual( matched[ii], matchedBidders[iii] ) ) {
              exists = true;
              break;
            }
          }

          if ( ! exists ) {
            matchedBidders.push( matched[ii] );
          }
        } else {
          matchedBidders.push( matched[ii] );
        }
      }
    }

    return matchedBidders;
  }

  /**
   * Gets the bidder data for a particular ad size
   *
   * @param size            - the ad size we want bidder data for
   * @param bidders         - the available bidders for the zone
   * @returns {Promise<[]>} - the matched bidders
   */
  static async getBidders( size, bidders ) {
    const matchedBidders = [];

    for ( let i = 0; i < bidders.length; i++ ) {
      const adSizes = bidders[i].adSizes;

      for ( let ii = 0; ii < adSizes.length; ii++ ) {
        if ( adSizes[ii][0] === size ) {
          matchedBidders.push( {
              bidder: bidders[i].name,
              params: adSizes[ii][1]
            }
          );
        }
      }
    }

    return matchedBidders;
  }

  /**
   * Gets the available bidders for a particular zone
   *
   * @param zone            - DFP Zone
   * @param placements      - Responsive Prebid placement config
   * @returns {Promise<[]>} - available bidders
   */
  static async getAvailableBidders( zone, placements ) {
    for ( let i = 0; i < placements.length; i++ ) {
      if ( placements[i].id === zone ) {
        return placements[i].bidders;
      }
    }
  }

}